import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, LayoutDashboard, Shirt, ShoppingCart, SlidersHorizontal, Globe, Paintbrush, Truck, Mailbox } from 'lucide-react';
import { AuthContext } from '../App';
import AppHeader from '../components/AppHeader';
import { errMsg, StatusPill, btn } from '../components/stores/ui';
import { StoreOverview } from '../components/stores/StoreOverview';
import { StoreProducts } from '../components/stores/StoreProducts';
import { StoreOrders } from '../components/stores/StoreOrders';
import { StoreCheckoutSettings } from '../components/stores/StoreCheckoutSettings';
import { StoreShipping } from '../components/stores/StoreShipping';
import { StoreDomainSettings } from '../components/stores/StoreDomainSettings';
import { StoreBranding } from '../components/stores/StoreBranding';
import { StoreEddm } from '../components/stores/StoreEddm';

const TABS = [['overview', 'Overview', LayoutDashboard], ['products', 'Products', Shirt], ['orders', 'Orders', ShoppingCart], ['shipping', 'Shipping', Truck], ['eddm', 'EDDM mail', Mailbox], ['checkout', 'Checkout & payments', SlidersHorizontal], ['domain', 'Domain settings', Globe], ['branding', 'Branding', Paintbrush]];

export default function StoreDashboard() {
  const { id, tab = 'overview' } = useParams();
  const nav = useNavigate();
  const { user } = useContext(AuthContext);
  const [store, setStore] = useState(null);
  const load = useCallback(() => axios.get(`/stores/${id}`).then((r) => setStore(r.data)).catch((e) => { toast.error(errMsg(e, 'Store not found')); nav('/stores'); }), [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!store) return <div className="min-h-screen" style={{ background: '#0A0A0A' }}><AppHeader /></div>;
  const previewUrl = `${store.urls.preview_path}`;
  const props = { store, reload: load };
  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff' }} data-testid="store-dashboard">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Link to="/stores" style={{ color: '#A1A1AA', fontSize: 13, display: 'inline-flex', gap: 6, alignItems: 'center', textDecoration: 'none' }} data-testid="back-to-stores"><ArrowLeft size={14} /> All stores</Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, margin: '8px 0 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 44, height: 44, borderRadius: 10, background: store.branding?.primary_color || '#1e293b', display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 20 }}>{store.name[0]}</span>
            <div>
              <h1 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 30, textTransform: 'uppercase', margin: 0, lineHeight: 1 }} data-testid="store-title">{store.name}</h1>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                <StatusPill status={store.status} testId="store-status" />
                <StatusPill status={store.payments.effective_mode} testId="store-payment-mode" />
                <a href={store.urls.primary_url} target="_blank" rel="noreferrer" style={{ color: '#A5B4FC', fontSize: 13 }} data-testid="store-primary-host">{store.urls.primary_host}</a>
              </div>
            </div>
          </div>
          <a href={previewUrl} target="_blank" rel="noreferrer" style={btn} data-testid="open-storefront-btn"><ExternalLink size={14} /> Open storefront</a>
        </div>
        <nav style={{ display: 'flex', gap: 4, borderBottom: '1px solid #27272A', marginBottom: 20, overflowX: 'auto' }}>
          {TABS.map(([k, t, I]) => (
            <Link key={k} to={`/stores/${id}/${k}`} data-testid={`store-tab-${k}`} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '10px 14px', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', textDecoration: 'none',
              color: tab === k ? '#fff' : '#A1A1AA', borderBottom: tab === k ? '2px solid #6366F1' : '2px solid transparent' }}><I size={14} /> {t}</Link>
          ))}
        </nav>
        {tab === 'overview' && <StoreOverview {...props} />}
        {tab === 'products' && <StoreProducts {...props} />}
        {tab === 'orders' && <StoreOrders {...props} />}
        {tab === 'shipping' && <StoreShipping {...props} isAdmin={user?.role === 'admin'} />}
        {tab === 'eddm' && <StoreEddm {...props} isAdmin={user?.role === 'admin'} />}
        {tab === 'checkout' && <StoreCheckoutSettings {...props} />}
        {tab === 'domain' && <StoreDomainSettings {...props} />}
        {tab === 'branding' && <StoreBranding {...props} />}
      </main>
    </div>
  );
}
